import React, { createContext, useContext, useEffect, useMemo, useRef, useCallback } from "react";
import { inject, observer } from "mobx-react";
import { LLMClient, useLLMClient, SSE_EVENT_TYPES, STREAM_TIMEOUT } from "../../llm/LLMClient";
import { ChatStore } from "../../llm/ChatStore";
import { useSSEListener } from "../../hooks/useSSEListener";
import useWatchdog from "../../hooks/useWatchdog";
import { resolveSessionAgent } from "../../llm/agentGating";
import useCoachEntitled from "../../llm/useCoachEntitled";
import { getConfig } from "../../lib/configMgr";
import { getStreamSseURL, getStreamURL } from "../../queries/search";

/**
 * ConversationStreamProvider — owns the chat engine(s) at the ProjectView
 * level so the SSE connection survives chat-rail navigation.
 *
 * Two implementations, selected by the REACT_APP_CHAT_STORE flag:
 *
 *  - LEGACY (flag off, default): one hoisted LLMClient. The SSE is driven by
 *    useLLMClient's useSSEListener keyed to client.streamId; navigation nulls
 *    streamId (via useChatLoader.clear()) which CLOSES + later reconnects the
 *    stream — the churn that exhausts the backend's a2wsgi 10-thread pool and
 *    surfaces as a 503 (see docs/design-chatstore-decouple-sse.md).
 *
 *  - CHAT-STORE (flag on): a ChatStore owns engines keyed by chatId. Navigation
 *    is store.setActive(chatId) — a pure view switch that never closes a live
 *    stream. A single SSE listener (useEngineStream) binds to store.liveEngine
 *    (the streaming chat) rather than the displayed one, so switching chats
 *    never re-binds/closes it. "Don't close the old stream."
 */

const ConversationStreamContext = createContext(null);

const { withAutoplex } = getConfig();

// Flag for the ChatStore (decoupled-connection) path. ON for next-gen (the
// isolated validation cell) by default; `REACT_APP_CHAT_STORE=true` forces it for
// any build; a localStorage override ('1' on / '0' off) allows A/B + an instant
// kill switch without a redeploy (takes effect on reload, since the provider is
// selected at module load). OFF everywhere else — prod cells, dev, tests —
// until validated on next-gen.
const chatStoreEnabled = () => {
  try {
    const ls = (typeof window !== "undefined" && window.localStorage)
      ? window.localStorage.getItem("workspace.chatStore") : null;
    if (ls === "1") return true;
    if (ls === "0") return false;
  } catch (_) { /* ignore */ }
  if (process.env.REACT_APP_CHAT_STORE === "true") return true;
  try { return getConfig().cell === "next-gen"; } catch (_) { return false; }
};
const CHAT_STORE_ENABLED = chatStoreEnabled();

// ───────────────────────────── LEGACY (flag off) ─────────────────────────────
const ConversationStreamProviderBase = ({ userStore, entitlementsStore, children }) => {
  const coachEntitled = useCoachEntitled(userStore, entitlementsStore);
  const sessionAgent = resolveSessionAgent({
    withAutoplex,
    coachEntitled,
  });
  const client = useLLMClient(sessionAgent, null, userStore?.authData, userStore);

  useEffect(() => {
    if (!client) return undefined;
    const onNewChat = () => {
      try {
        if (client.streamId || client.waitingForResponse || client.active) {
          client.interrupt?.();
        }
        client.clear?.();
      } catch (_) { /* never break the chat surface */ }
    };
    window.addEventListener("workspace:new-chat", onNewChat);
    return () => window.removeEventListener("workspace:new-chat", onNewChat);
  }, [client]);

  return (
    <ConversationStreamContext.Provider value={{ client, store: null }}>
      {children}
    </ConversationStreamContext.Provider>
  );
};

// ───────────────────────────── CHAT-STORE (flag on) ──────────────────────────
// PLEX-6261: the stream's /sse path, with the old URL as the fallback for a backend
// deployed before it. The same URLs as useLLMClient's connector (useStreamConnector):
// this path has its own SSE listener, and missed a connector fix once before (Tim, on #91).

/**
 * Runs ONE SSE listener bound to the store's LIVE engine (the streaming chat),
 * not the displayed one. Reading store.liveEngine during this render makes the
 * (observer) provider reactive: when a turn starts (setLive) or ends
 * (streamId → null), liveEngine + its url change and useSSEListener opens/closes
 * accordingly. Navigation changes only the *active* engine, never liveEngine, so
 * it never touches the open connection. Reuses the proven useSSEListener.
 */
const useEngineStream = (store, userStore) => {
  const live = store.liveEngine;                 // observed → reactive
  const streamId = live?.streamId || null;
  // PLEX-4218: /api/stream/{channel} requires auth (backend commit 290cae794).
  // This is a SEPARATE useSSEListener call from useLLMClient's (the ChatStore
  // path drives its own listener off store.liveEngine rather than a single
  // hoisted client) — the same header threading has to be repeated here.
  const authHeaders = userStore?.jwtToken
                     ? {authorization: `Bearer ${userStore.jwtToken}`}
                     : undefined;
  const [resetWatchdog, stopWatchdog] = useWatchdog(
    () => store.liveEngine?._handleTimeout?.(),
    STREAM_TIMEOUT,
  );
  // store.liveEngine is re-read inside each callback (a MobX getter), so the
  // handlers always target the current live engine without being recreated when
  // the stream changes — streamId is deliberately NOT a dependency here.
  const onMessage = useCallback((e) => {
    resetWatchdog();
    store.liveEngine?.handleStreamEvent(e);
  }, [store, resetWatchdog]);
  const onError = useCallback((e) => {
    stopWatchdog();
    store.liveEngine?.handleStreamEvent(e);
  }, [store, stopWatchdog]);
  // autoReconnect + lastEventId ARE honored by useSSEListener: autoReconnect
  // swaps in ResumableEventSource, which re-sends Last-Event-ID on a transport
  // reconnect; live._lastEventId is populated by LLMClient.handleStreamEvent on
  // every event (same as the legacy useLLMClient path). Stream resume is wired —
  // these are not dead params.
  const eventSource = useSSEListener({
    url: getStreamSseURL(streamId),
    fallbackUrl: getStreamURL(streamId),
    onMessage,
    onError,
    eventTypes: SSE_EVENT_TYPES,
    lastEventId: live?._lastEventId || "",
    autoReconnect: true,
    headers: authHeaders,
  });
  const esRef = useRef(eventSource);
  useEffect(() => { esRef.current = eventSource; }, [eventSource]);
  // explicit Stop nulls streamId → _closeStream() closes this connection.
  // Cleanup on a live A→B transition: null the prior engine's handler so a late
  // A._closeStream() can't reach through the shared esRef and close B's stream.
  useEffect(() => {
    if (live) live.onCloseStream = () => esRef.current?.close();
    return () => { if (live) live.onCloseStream = null; };
  }, [live]);
  useEffect(() => {
    if (streamId) resetWatchdog(); else stopWatchdog();
  }, [streamId]); // eslint-disable-line react-hooks/exhaustive-deps
};

const StoreProviderBase = ({ userStore, entitlementsStore, children }) => {
  const coachEntitled = useCoachEntitled(userStore, entitlementsStore);
  const sessionAgent = resolveSessionAgent({
    withAutoplex,
    coachEntitled,
  });
  // Factory reads auth/userStore via refs so engines created later pick up the
  // current values without recreating the store.
  const authRef = useRef(userStore?.authData);
  const userStoreRef = useRef(userStore);
  useEffect(() => { authRef.current = userStore?.authData; userStoreRef.current = userStore; });

  const store = useMemo(
    () => new ChatStore((/* chatId */) => new LLMClient({
      sessionAgent,
      auth: authRef.current,
      userStore: userStoreRef.current,
    })),
    [sessionAgent],
  );

  useEffect(() => () => store.disposeAll(), [store]);
  useEngineStream(store, userStore);

  useEffect(() => {
    const onNewChat = () => {
      try {
        const e = store.liveEngine || store.active;
        if (e && (e.streamId || e.waitingForResponse || e.active)) e.interrupt?.();
        // interrupt() is fire-and-forget (POSTs a cancel); clear() still resets
        // the engine's local state (turns / streamId / plan) for the fresh chat.
        e?.clear?.();
        store.setLive(null);
      } catch (_) { /* never break the surface */ }
    };
    window.addEventListener("workspace:new-chat", onNewChat);
    return () => window.removeEventListener("workspace:new-chat", onNewChat);
  }, [store]);

  return (
    <ConversationStreamContext.Provider value={{ client: store.active, store }}>
      {children}
    </ConversationStreamContext.Provider>
  );
};

const ConversationStreamProvider = inject("userStore", "entitlementsStore")(
  observer(CHAT_STORE_ENABLED ? StoreProviderBase : ConversationStreamProviderBase),
);

/**
 * Legacy consumer hook — the single hoisted client (null on the store path,
 * where ChatSurface reads the store instead).
 */
export const useConversationStreamClient = () => {
  const ctx = useContext(ConversationStreamContext);
  return ctx?.client || null;
};

/** The ChatStore when the flag is on, else null. */
export const useChatStore = () => {
  const ctx = useContext(ConversationStreamContext);
  return ctx?.store || null;
};

export default ConversationStreamProvider;
export { ConversationStreamContext, CHAT_STORE_ENABLED };
